import type { StandingRow } from '@airmail/engine';
import { cx } from '@/ui';
import { locale, useT } from '@/i18n';
import { useTm } from '@/i18n/engine';
import type { TeamLookup } from '@/lib/teams';

/**
 * Tabelle je Gruppe (docs/06 §7): Sp · S · U · N · + · − · Diff · Pkt,
 * Qualifikationsplätze markiert, Tiebreaker-Begründung als Fußnote.
 */
export function StandingsTable({
  rows: rawRows,
  lookup,
  qualifiers,
  compact,
  onLot,
  pending,
  highlight,
}: {
  rows: StandingRow[];
  lookup: TeamLookup;
  qualifiers: number;
  compact?: boolean;
  onLot?: () => void;
  /** Gruppe noch nicht komplett gespielt → Los-Hinweise ausblenden */
  pending?: boolean;
  /** „Mein Team" in der Zuschauer-Ansicht (docs/17 §9) — die Zeile wird blau unterlegt */
  highlight?: string;
}) {
  const t = useT();
  // Fußnoten kommen deutsch aus der Engine und werden erst beim Rendern übersetzt (tm)
  const tm = useTm();
  // tiebreakNotes kommen deutsch aus der Engine — der Filter bleibt deshalb deutsch
  const rows = pending ? rawRows.map((r) => ({ ...r, needsLot: false, tiebreakNotes: r.tiebreakNotes.filter((n) => !/Los|Gleichstand/.test(n)) })) : rawRows;
  const notes: string[] = [];
  const noteIndex = new Map<string, number>();
  for (const r of rows) {
    if (r.tiebreakNotes.length === 0) continue;
    const key = r.tiebreakNotes.join(' · ');
    if (!noteIndex.has(key)) {
      notes.push(key);
      noteIndex.set(key, notes.length);
    }
  }
  const lotNeeded = rows.some((r) => r.needsLot);
  // Ungleich viele Spiele in der Gruppe: Rang nach Quote — dann auch die Quoten zeigen (Harald, 10/2026)
  const quote = rows.some((r) => r.perGame);
  const zahl = (v: number) => v.toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line">
            <th className="label font-normal text-left px-2 py-1.5 w-8"> </th>
            {/* Einzelturnier: „Teilnehmer“ statt „Team“ (Harald, 10/2026) */}
            <th className="label font-normal text-left px-2 py-1.5">{rows.length > 0 && rows.every((r) => (lookup(r.teamId)?.players.length ?? 0) <= 1) ? t('Teilnehmer') : t('Team')}</th>
            <th className="label font-normal text-right px-2 py-1.5">{t('Sp')}</th>
            <th className="label font-normal text-right px-2 py-1.5">{t('S')}</th>
            {!compact && <th className="label font-normal text-right px-2 py-1.5">{t('U')}</th>}
            <th className="label font-normal text-right px-2 py-1.5">{t('N')}</th>
            {!compact && (
              <>
                <th className="label font-normal text-right px-2 py-1.5" title={t('Siege minus Niederlagen')}>
                  {t('±Sp')}
                </th>
                <th className="label font-normal text-right px-2 py-1.5">{t('+')}</th>
                <th className="label font-normal text-right px-2 py-1.5">{t('−')}</th>
              </>
            )}
            <th className="label font-normal text-right px-2 py-1.5">{t('Diff')}</th>
            <th className="label font-normal text-right px-2 py-1.5">{t('Pkt')}</th>
            {quote && (
              <>
                <th className="label font-normal text-right px-2 py-1.5" title={t('Siege ÷ Spiele — zählt, weil nicht alle gleich viele Spiele haben')}>
                  {t('Quote')}
                </th>
                <th className="label font-normal text-right px-2 py-1.5" title={t('Punktdifferenz ÷ Spiele')}>
                  {t('Ø Diff')}
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const team = lookup(r.teamId);
            const n = r.tiebreakNotes.length ? noteIndex.get(r.tiebreakNotes.join(' · ')) : undefined;
            const gone = team?.status === 'withdrawn' || team?.status === 'disqualified';
            return (
              // Blau statt Grün: Grün heißt hier schon „qualifiziert" (linker Balken) — das eigene Team muss daneben erkennbar bleiben
              <tr
                key={r.teamId}
                data-team={r.teamId}
                className={cx('border-b border-line2', r.qualified && 'border-l-2 border-l-live', r.needsLot && 'bg-warn/10', r.teamId === highlight && 'bg-blue/15', gone && 'opacity-50')}
              >
                <td className="px-2 py-1.5 font-mono text-[11px] text-dim">{r.rank}</td>
                <td className={cx('px-2 py-1.5 font-bold tracking-tight', r.teamId === highlight && 'text-blue')}>
                  {compact ? team?.shortName : team?.name}
                  {n !== undefined && <sup className="text-warn ml-0.5 font-normal">{n}</sup>}
                  {r.needsLot && <span className="label text-warn ml-2">{t('Los|Tabelle')}</span>}
                </td>
                <td className="px-2 py-1.5 text-right font-mono">{r.played}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.wins}</td>
                {!compact && <td className="px-2 py-1.5 text-right font-mono">{r.draws}</td>}
                <td className="px-2 py-1.5 text-right font-mono">{r.losses}</td>
                {!compact && (
                  <>
                    <td className="px-2 py-1.5 text-right font-mono">{r.wins - r.losses > 0 ? `+${r.wins - r.losses}` : r.wins - r.losses}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.scoreFor}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.scoreAgainst}</td>
                  </>
                )}
                <td className="px-2 py-1.5 text-right font-mono">{r.scoreDiff > 0 ? `+${r.scoreDiff}` : r.scoreDiff}</td>
                <td className={cx('px-2 py-1.5 text-right font-mono', !quote && 'font-bold')}>{r.points}</td>
                {quote && (
                  <>
                    <td className="px-2 py-1.5 text-right font-mono font-bold" data-quote>
                      {r.played > 0 ? zahl(r.wins / r.played) : '–'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.played > 0 ? `${r.scoreDiff > 0 ? '+' : ''}${zahl(r.scoreDiff / r.played)}` : '–'}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {(notes.length > 0 || lotNeeded) && (
        <div className="font-mono text-[11px] text-dim mt-1.5 leading-relaxed">
          {notes.map((n, i) => (
            <div key={n}>
              <sup className="text-warn">{i + 1}</sup> {n.split(' · ').map(tm).join(' · ')}
            </div>
          ))}
          {lotNeeded && (
            <div className="text-warn">
              {t('Gleichstand nicht auflösbar —')}{' '}
              {onLot ? (
                <button className="underline cursor-pointer" onClick={onLot}>
                  {t('jetzt losen')}
                </button>
              ) : (
                t('Los erforderlich|Tabelle')
              )}
            </div>
          )}
        </div>
      )}
      {qualifiers > 0 && rows.length > 0 && <div className="label mt-1.5">{t('Top {n} qualifiziert', { n: qualifiers })}</div>}
    </div>
  );
}
